import { Logout } from '@mui/icons-material'
import AccountBoxIcon from '@mui/icons-material/AccountBox'
import BoltIcon from '@mui/icons-material/Bolt'
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead'
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant'
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'
import { Box, Button, Card, CardActionArea, CardContent, Divider, Stack, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import React from 'react'
import ApiClient from '../../backend/ApiClient'
import LocalCache from '../../backend/LocalCache'
import { useAppDispatch, useAppSelector } from '../../store/Hooks'
import { setCurrentUser } from '../../store/slices/AppSlice'
import { setJWT } from '../../store/slices/SessionSlice'
import TabsHeader, { ITabItem } from '../controls/TabsHeader'
import SmartCard from '../controls/cards/SmartCard'
import Tile from '../layout/Tile'
import { modals } from '../services/ModalService'
import { snackbar } from '../services/SnackbarService'
import SettingsTab from './SettingsTab'
import UsersManager from '../../managers/UsersManager'

const ProfileContainer = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 20
}))

const avatarStyle: React.CSSProperties = {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '2px solid #ddd'
}

const ProfileInfo = () => {
    const user = UsersManager.getCurrentUser()
    const dispatch = useAppDispatch()
    const handleLogout = async () => {
        modals.openConfirm({
            title: 'Confirm Logging out',
            children: <p>Are you sure you want to log out?</p>,
            labels: { confirm: 'Log out', cancel: 'Cancel' },
            colors: { confirm: 'error', cancel: 'primary' },
            onConfirm: async () => {
                try {
                    const redirect = window.location.origin
                    const url = await ApiClient.Auth.getCognitoLogoutURL(redirect)
                    window.location.href = url
                    dispatch(setCurrentUser(null))
                    dispatch(setJWT(null))
                    LocalCache.clearAll()
                } catch (error) {
                    console.error('Error removing users:', error)
                    snackbar.show({ severity: 'error', text: 'Logout failed' })
                }
            }
        })
    }

    return (
        <SmartCard>
            <Stack direction='row' spacing={2} alignItems='center' justifyContent='space-between'>
                <Box flexDirection='row' display='flex' alignItems='center' gap={2}>
                    <AccountBoxIcon fontSize='large' />
                    <Typography variant='h5' fontWeight='bold'>Profile Info</Typography>
                </Box>
                <Button
                    variant='outlined'
                    color='error'
                    startIcon={<Logout />}
                    onClick={handleLogout}>
                    Logout
                </Button>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Box display='flex' flexDirection='column' gap={2}>
                <img src={user?.image} alt='avatar' style={avatarStyle} />
                <Box
                    display='grid'
                    gap={1.5}
                    gridTemplateColumns='200px 1fr'>
                    <Typography variant='important'>Fullname:</Typography>
                    <Typography variant='body1'>{user?.fullName}</Typography>

                    <Typography variant='important'>Email address:</Typography>
                    <Typography variant='body1'>{user?.email}</Typography>

                    <Typography variant='important'>Role:</Typography>
                    <Typography variant='body1'>{user?.roles ?? 'No Role'}</Typography>

                </Box>
            </Box>
        </SmartCard>
    )
}

const QuickAccessSettings = () => {
    return (
        <SmartCard>
            <Box display='flex'>
                <BoltIcon fontSize='large' />
                <Typography variant='h6' mb={2}>Quick Access Settings</Typography>
            </Box>
            <Box display='flex' gap={2}>
                <Card sx={{ flex: 1 }}>
                    <CardActionArea
                        sx={{ display: 'block', height: '100%' }}
                        onClick={() => {
                            snackbar.show({
                                severity: 'info',
                                text: 'This functionality is currently under development.'
                            })
                        }}>
                        <CardContent>
                            <MarkEmailReadIcon fontSize='large' color='primary' />
                            <Typography variant='body1'>Change Email Address</Typography>
                            <Typography variant='body2' color='text.secondary'>
                                Quickly change your email address
                            </Typography>
                        </CardContent>
                    </CardActionArea>
                </Card>
                <Card sx={{ flex: 1 }}>
                    <CardActionArea
                        sx={{ display: 'block', height: '100%' }}
                        onClick={() => {
                            snackbar.show({
                                severity: 'info',
                                text: 'This functionality is currently under development.'
                            })
                        }}>
                        <CardContent onClick={() => snackbar.show({ severity: 'info', text: 'This functionality is currently under development.' })}>
                            <VerifiedUserIcon fontSize='large' color='primary' />
                            <Typography variant='body1'>Change Password</Typography>
                            <Typography variant='body2' color='text.secondary'>
                                Quickly update your password
                            </Typography>
                        </CardContent>
                    </CardActionArea>
                </Card>
                <Card sx={{ flex: 1 }}>
                    <CardActionArea
                        sx={{ display: 'block', height: '100%' }}
                        onClick={() => {
                            snackbar.show({
                                severity: 'info',
                                text: 'This functionality is currently under development.'
                            })
                        }}>
                        <CardContent>
                            <NotificationImportantIcon fontSize='large' color='primary' />
                            <Typography variant='body1'>Change Notification Settings</Typography>
                            <Typography variant='body2' color='text.secondary'>
                                Quickly change your notification settings
                            </Typography>
                        </CardContent>
                    </CardActionArea>

                </Card>
            </Box>
        </SmartCard>
    )
}

const UserProfileTabs = () => {
    const routeParams = useAppSelector(state => state.session.routeParams)
    const defaultTab = routeParams?.tab ?? 'profile'
    const [selectedTab, setSelectedTab] = React.useState<string>(defaultTab)
    // const currentUser = useAppSelector(state => state.app.currentUser)
    // const isAdmin: boolean = currentUser && currentUser.roles && currentUser.roles.includes('master')

    const tabs: ITabItem[] = [
        {
            id: 'profile',
            name: 'Profile',
            renderPanel: () => {
                return (
                    <ProfileContainer>
                        <ProfileInfo />
                        <QuickAccessSettings />
                    </ProfileContainer>
                )
            }
        },
        // {
        //     id: 'activity',
        //     name: 'Activity',
        //     renderPanel: () => <ActivityTab />
        // },
        {
            id: 'settings',
            name: 'Settings',
            renderPanel: () => <SettingsTab />
        }
    ]

    return (
        <Tile.Container>
            <Box mb={2}>
                <TabsHeader
                    tabs={tabs}
                    defaultSelectedTab={selectedTab}
                    onTabChange={(tabId: string) => setSelectedTab(tabId)}
                />
            </Box>
        </Tile.Container>
    )
}

export default UserProfileTabs
